<script lang="ts">
    import type { Snippet } from 'svelte';

    interface SrcsetEntry {
        url: string;
        width: number;
    }

    interface Props {
        imageUrl: string | null;
        imageSrcset?: SrcsetEntry[];
        topicName?: string | null;
        historyExcerpt: string;
        institution?: string | null;
        answerStatus?: string | null;
        hoverZoom?: boolean;
        meta?: Snippet;
    }

    let {
        imageUrl,
        imageSrcset = [],
        topicName = null,
        historyExcerpt,
        institution = null,
        answerStatus = null,
        hoverZoom = false,
        meta,
    }: Props = $props();
</script>

{#snippet imagePlaceholder()}
    <div class="w-full h-full flex items-center justify-center text-gray-600">
        <svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.41a2.25 2.25 0 013.182 0l2.909 2.91M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
        </svg>
    </div>
{/snippet}

<div class="relative aspect-[4/3] bg-gray-800 overflow-hidden" >
    {#if imageUrl}
        <img
            src={imageUrl}
            srcset={imageSrcset.length ? imageSrcset.map(s => `${s.url} ${s.width}w`).join(', ') : undefined}
            sizes={imageSrcset.length ? '(max-width: 640px) 100vw, 400px' : undefined}
            alt=""
            class="w-full h-full object-cover {hoverZoom ? 'group-hover:scale-105 transition-transform duration-200' : ''}"
        />
    {:else}
        {@render imagePlaceholder()}
    {/if}
    {#if topicName}
        <span class="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-900/90 text-gray-300 shadow-sm">
            <svg class="size-3" viewBox="0 0 576 512" fill="currentColor"><path d="M96.1 88c0-13.3 10.7-24 24-24l156.1 0c6.4 0 12.5 2.5 17 7l208 208c9.4 9.4 9.4 24.6 0 33.9L345.1 469.1c-9.4 9.4-24.6 9.4-33.9 0l-208-208c-4.5-4.5-7-10.6-7-17L96.1 88zm24-72c-39.8 0-72 32.2-72 72l0 156.1c0 19.1 7.6 37.4 21.1 50.9l208 208c28.1 28.1 73.7 28.1 101.8 0L535.1 346.9c28.1-28.1 28.1-73.7 0-101.8l-208-208C313.6 23.6 295.3 16 276.2 16L120.1 16zm56 160a32 32 0 1 0 0-64 32 32 0 1 0 0 64z"/></svg>
            {topicName}
        </span>
    {/if}
    {#if answerStatus === 'correct'}
        <span class="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-600/90 text-white shadow-sm">
            <svg class="size-3" viewBox="0 0 512 512" fill="currentColor"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209L241 337c-9.4 9.4-24.6 9.4-33.9 0l-64-64c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l47 47L335 175c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9z"/></svg>
            Correct
        </span>
    {:else if answerStatus === 'incorrect'}
        <span class="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-600/90 text-white shadow-sm">
            <svg class="size-3" viewBox="0 0 512 512" fill="currentColor"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM175 175c9.4-9.4 24.6-9.4 33.9 0l47 47 47-47c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-47 47 47 47c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0l-47-47-47 47c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l47-47-47-47c-9.4-9.4-9.4-24.6 0-33.9z"/></svg>
            Incorrect
        </span>
    {:else if answerStatus === 'pending'}
        <span class="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-yellow-600/90 text-white shadow-sm">
            <svg class="size-3" viewBox="0 0 512 512" fill="currentColor"><path d="M256 0a256 256 0 1 1 0 512A256 256 0 1 1 256 0zM232 120l0 136c0 8 4 15.5 10.7 20l96 64c11 7.4 25.9 4.4 33.3-6.7s4.4-25.9-6.7-33.3L280 243.2 280 120c0-13.3-10.7-24-24-24s-24 10.7-24 24z"/></svg>
            Pending
        </span>
    {:else if answerStatus === 'answered'}
        <span class="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-cyan-600/90 text-white shadow-sm">
            <svg class="size-3" viewBox="0 0 512 512" fill="currentColor"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209L241 337c-9.4 9.4-24.6 9.4-33.9 0l-64-64c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l47 47L335 175c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9z"/></svg>
            Answered
        </span>
    {:else if answerStatus === 'unanswered'}
        <span class="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-500/90 text-white shadow-sm">
            <svg class="size-3" viewBox="0 0 512 512" fill="currentColor"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-384c13.3 0 24 10.7 24 24l0 112c0 13.3-10.7 24-24 24s-24-10.7-24-24l0-112c0-13.3 10.7-24 24-24zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z"/></svg>
            Unanswered
        </span>
    {/if}
</div>
<div class="p-4 flex-1 flex flex-col">
    {#if meta}
        <div class="mb-2">
            {@render meta()}
        </div>
    {/if}
    <p class="text-sm text-gray-300 line-clamp-3 flex-1">{historyExcerpt}</p>
    {#if institution}
        <p class="text-xs text-gray-400 mt-2">{institution}</p>
    {/if}
</div>
